import { getMonthlyProjectionSeries } from '@financego/core';

import type { Transaction } from '../../lib/api-types';
import type { DashboardSnapshot } from './dashboard.types';
import type { PlanFilters } from './plan-filter.types';

export interface PlanListItem {
  readonly amount: number;
  readonly date: string;
  readonly isAdjusted: boolean;
  readonly mode: 'occurrence' | 'series';
  readonly note: string;
  readonly transaction: Transaction;
  readonly walletId: string;
}

export function buildPlanListItems(
  snapshot: DashboardSnapshot,
  filters: PlanFilters,
): readonly PlanListItem[] {
  const transactionById = new Map(
    snapshot.rawTransactions.map((transaction) => [transaction.id, transaction]),
  );
  const movements = getMonthlyProjectionSeries(
    snapshot.coreTransactions,
    filters.year,
    filters.month,
    { ignoreValidation: true, includeTransfers: true },
  ).flatMap((point) => point.movements);
  const occurrences: PlanListItem[] = [];
  for (const movement of movements) {
    const transaction =
      movement.transactionId === null ? undefined : transactionById.get(movement.transactionId);
    if (transaction === undefined || !matchesMovementFilter(transaction, filters.movement)) {
      continue;
    }
    const override = transaction.overrides.find((item) => item.date === movement.date);
    const overrideNote = override?.additionalNote?.trim();
    occurrences.push({
      amount: movement.amount,
      date: movement.date,
      isAdjusted: override !== undefined,
      mode: 'occurrence',
      note:
        overrideNote === undefined || overrideNote.length === 0 ? transaction.note : overrideNote,
      transaction,
      walletId: movement.walletId ?? transaction.walletId,
    });
  }

  if (filters.showOccurrences) return occurrences;

  const firstOccurrence = new Map<string, PlanListItem>();
  for (const occurrence of occurrences) {
    if (!firstOccurrence.has(occurrence.transaction.id)) {
      firstOccurrence.set(occurrence.transaction.id, occurrence);
    }
  }

  return [...firstOccurrence.values()].map((occurrence) => ({
    ...occurrence,
    amount: occurrence.transaction.amount,
    isAdjusted: false,
    mode: 'series',
    note: occurrence.transaction.note,
    walletId: occurrence.transaction.walletId,
  }));
}

function matchesMovementFilter(transaction: Transaction, filter: PlanFilters['movement']): boolean {
  if (filter === 'recurring') {
    return transaction.type !== 'transfer' && transaction.frequency !== 'one-time';
  }
  if (filter === 'one-time') {
    return transaction.type !== 'transfer' && transaction.frequency === 'one-time';
  }
  if (filter === 'transfers') return transaction.type === 'transfer';
  return true;
}
